import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import { Logo } from "@/components/AppLayout";
import { Field } from "@/pages/Login";
import { COUNTRIES } from "@/lib/countries";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import { ThemeToggle } from "@/components/ThemeToggle";
import { PublicMobileNav } from "@/components/PublicMobileNav";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [form, setForm] = useState({
    name: "", email: "", phone: "", country: "", password: "", confirm: "",
  });
  const [busy, setBusy] = useState(false);

  const validBasic = () => {
    if (!form.name.trim()) { toast.error("Full name required"); return false; }
    if (!form.email.trim()) { toast.error("Email required"); return false; }
    if (!form.country) { toast.error("Please select your country"); return false; }
    if (form.password.length < 6) { toast.error("Password must be at least 6 characters"); return false; }
    if (form.password !== form.confirm) { toast.error("Passwords do not match"); return false; }
    return true;
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!validBasic()) return;
    setBusy(true);
    const r = await register({
      name: form.name, email: form.email.trim().toLowerCase(),
      phone: form.phone, country: form.country, password: form.password,
    });
    setBusy(false);
    if (r.ok) { toast.success("Account created"); nav("/dashboard", { replace: true }); }
    else toast.error(r.error);
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-4 sm:px-6 py-8 sm:py-12 relative overflow-hidden">
      <div className="absolute top-4 left-4 z-20"><PublicMobileNav /></div>
      <div className="absolute top-4 right-4 z-20"><ThemeToggle /></div>
      <div className="absolute inset-0 grid-bg opacity-40 pointer-events-none" />
      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[400px] sm:w-[700px] max-w-[120vw] h-[300px] sm:h-[400px] bg-[#007AFF]/15 blur-[100px] sm:blur-[120px] rounded-full pointer-events-none" />

      <div className="relative z-10 w-full max-w-lg">
        <div className="flex justify-center mb-6 sm:mb-8"><Logo /></div>
        <div className="glass-strong rounded-2xl p-6 sm:p-8">
          <h2 className="font-display text-2xl font-bold tracking-tight">Create an account</h2>
          <p className="text-[#94A3B8] text-sm mt-1">Join the SpectralX client portal</p>

          <form onSubmit={submit} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <Field label="Full Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testid="reg-name" />
            <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testid="reg-email" />
            <Field label="Mobile" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} testid="reg-phone" />

            <label className="block">
              <span className="text-xs uppercase tracking-[0.18em] text-[#94A3B8] font-bold">Country</span>
              <Select value={form.country} onValueChange={(v) => setForm({ ...form, country: v })}>
                <SelectTrigger
                  data-testid="reg-country"
                  className="mt-2 w-full bg-[#0A132A] border border-white/10 rounded-lg px-4 h-[42px] text-white focus:outline-none focus:border-[#00F0FF] focus:ring-1 focus:ring-[#00F0FF] data-[placeholder]:text-[#475569]"
                >
                  <SelectValue placeholder="Select country" />
                </SelectTrigger>
                <SelectContent
                  className="bg-[#0A132A] border-white/10 text-white max-h-72"
                >
                  {COUNTRIES.map((c) => (
                    <SelectItem
                      key={c}
                      value={c}
                      className="focus:bg-[#00F0FF]/10 focus:text-[#00F0FF] data-[state=checked]:text-[#00F0FF]"
                      data-testid={`country-${c}`}
                    >
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </label>

            <Field label="Password" type="password" value={form.password} onChange={(v) => setForm({ ...form, password: v })} testid="reg-password" />
            <Field label="Confirm Password" type="password" value={form.confirm} onChange={(v) => setForm({ ...form, confirm: v })} testid="reg-confirm" />

            <button
              type="submit"
              disabled={busy}
              data-testid="register-submit"
              className="sm:col-span-2 w-full py-3 rounded-lg bg-[#00F0FF] text-[#060B19] font-bold hover:bg-[#00D4E0] hover:shadow-[0_0_24px_rgba(0,240,255,0.45)] transition-all disabled:opacity-50"
            >
              {busy ? "Creating…" : "Create Account"}
            </button>
          </form>

          <div className="text-sm text-[#94A3B8] text-center mt-6">
            Already have an account?{" "}
            <Link to="/login" className="text-[#00F0FF] hover:underline" data-testid="link-to-login">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}
